import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiChevronLeft, FiChevronRight, FiTrash2, FiEye, FiUser, FiStar, FiSend } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import {
  closeViewer,
  nextStory,
  prevStory,
  markFeedStoryViewed,
  selectStoryFeed,
  selectActiveGroupIndex,
  selectActiveStoryIndex,
  deleteStory,
} from '../../store/slices/storiesSlice';
import { markStoryViewed } from '../../store/slices/storiesSlice';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { selectHasProAccess } from '../../store/slices/subscriptionSlice';
import messagingApi from '../../store/api/messagingApi';
import type { StoryViewer as StoryViewerType } from '../../types';

const IMAGE_DURATION = 5000;
const REACTIONS = ['❤️', '🔥', '😍', '😂', '👏'];

export function StoryViewer() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const feed = useAppSelector(selectStoryFeed);
  const groupIndex = useAppSelector(selectActiveGroupIndex);
  const storyIndex = useAppSelector(selectActiveStoryIndex);
  const currentUser = useAppSelector(selectCurrentUser);
  const isPro = useAppSelector(selectHasProAccess);
  const isAdmin = currentUser?.role === 'ADMIN' || (currentUser as any)?.isAdmin === true;
  const canSeeViewers = isPro || isAdmin;

  const [viewersSheet, setViewersSheet] = useState<StoryViewerType[] | null>(null);
  const [proGate, setProGate] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sent, setSent] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(0);
  const durationRef = useRef<number>(IMAGE_DURATION);
  const pausedRef = useRef<boolean>(false);
  const remainingRef = useRef<number>(IMAGE_DURATION);

  const group = groupIndex !== null ? feed[groupIndex] : null;
  const story = group ? group.stories[storyIndex] : null;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const advanceStory = useCallback(() => {
    // Snap current progress bar to full before React re-renders it as a past story,
    // otherwise the lingering CSS transition causes it to slowly fill after skipping.
    if (progressRef.current) {
      progressRef.current.style.transition = 'none';
    }
    dispatch(nextStory());
  }, [dispatch]);

  const pauseProgress = useCallback(() => {
    if (pausedRef.current) return;
    pausedRef.current = true;
    clearTimer();
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(durationRef.current - elapsed, 0);
    if (progressRef.current) {
      const pct = Math.min(elapsed / durationRef.current, 1);
      progressRef.current.style.transition = 'none';
      progressRef.current.style.width = `${pct * 100}%`;
    }
  }, [clearTimer]);

  const resumeProgress = useCallback(() => {
    if (!pausedRef.current || !progressRef.current) return;
    pausedRef.current = false;
    const remaining = remainingRef.current;
    progressRef.current.getBoundingClientRect(); // force reflow
    progressRef.current.style.transition = `width ${remaining}ms linear`;
    progressRef.current.style.width = '100%';
    startTimeRef.current = Date.now() - (durationRef.current - remaining);
    clearTimer();
    timerRef.current = setTimeout(advanceStory, remaining);
  }, [advanceStory, clearTimer]);

  const handlePrev = useCallback(() => {
    // Clear transition so the skipped story's bar snaps to 0% instead of animating back.
    if (progressRef.current) {
      progressRef.current.style.transition = 'none';
      progressRef.current.style.width = '0%';
    }
    dispatch(prevStory());
  }, [dispatch]);

  useEffect(() => {
    if (!story) return;
    pausedRef.current = false;

    dispatch(markStoryViewed(story._id));
    dispatch(markFeedStoryViewed(story._id));

    const duration =
      story.media[0]?.type === 'video' && story.media[0].duration > 0
        ? story.media[0].duration * 1000
        : IMAGE_DURATION;

    durationRef.current = duration;
    startTimeRef.current = Date.now();

    if (progressRef.current) {
      progressRef.current.style.transition = 'none';
      progressRef.current.style.width = '0%';
      progressRef.current.getBoundingClientRect();
      progressRef.current.style.transition = `width ${duration}ms linear`;
      progressRef.current.style.width = '100%';
    }

    clearTimer();
    timerRef.current = setTimeout(advanceStory, duration);

    return () => clearTimer();
  }, [story, advanceStory, clearTimer, dispatch]);

  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(null), 2000);
    return () => clearTimeout(t);
  }, [sent]);

  if (!group || !story) return null;

  const media = story.media[0];
  const isOwnStory = currentUser && story.userId === currentUser._id;

  const handleDelete = () => {
    if (!currentUser) return;
    dispatch(deleteStory({ userId: currentUser._id, storyId: story._id }));
    dispatch(closeViewer());
  };

  const storyMediaUrl = media?.url;
  const storyMediaType = media?.type === 'video' ? 'video' : 'image';

  const handleReaction = async (emoji: string) => {
    try {
      await messagingApi.startConversation(story.userId, emoji, 'STORY_REACTION', storyMediaUrl, storyMediaType);
      setSent(`${emoji} Sent!`);
    } catch {
      setSent('Could not send');
    }
  };

  const handleReply = async () => {
    const text = replyText.trim();
    if (!text) return;
    setReplyText('');
    try {
      await messagingApi.startConversation(story.userId, text, 'STORY_REPLY', storyMediaUrl, storyMediaType);
      setSent('Reply sent!');
    } catch {
      setSent('Could not send');
    }
    resumeProgress();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Progress bars */}
      <div className="flex gap-1 px-3 pt-3 pb-2">
        {group.stories.map((s, i) => (
          <div key={s._id} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
            <div
              ref={i === storyIndex ? progressRef : undefined}
              className="h-full bg-white rounded-full"
              style={{ width: i < storyIndex ? '100%' : '0%' }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 px-4 pb-3">
        <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
          {group.profile?.profilePhoto ? (
            <img src={group.profile.profilePhoto} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-white/10" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">
            {group.profile?.firstName ?? 'User'}
          </p>
          <p className="text-white/50 text-xs">
            {new Date(story.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        {isOwnStory && (
          <button
            onClick={handleDelete}
            className="p-1 text-white/60 hover:text-red-400 transition-colors mr-1"
            aria-label="Delete story"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => dispatch(closeViewer())}
          className="p-1 text-white/70 hover:text-white transition-colors"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      {/* Media */}
      <div className="flex-1 relative">
        {media?.type === 'video' ? (
          <video
            key={story._id}
            src={media.url}
            autoPlay
            playsInline
            muted={false}
            className="absolute inset-0 w-full h-full object-contain"
            onEnded={advanceStory}
          />
        ) : (
          <img
            key={story._id}
            src={media?.url}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
          />
        )}

        {/* Tap zones */}
        <button
          className="absolute left-0 inset-y-0 w-1/3"
          onClick={handlePrev}
          aria-label="Previous"
        />
        <button
          className="absolute right-0 inset-y-0 w-1/3"
          onClick={advanceStory}
          aria-label="Next"
        />
      </div>

      {/* Caption */}
      {story.caption && (
        <div className="px-5 py-3 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-sm">{story.caption}</p>
        </div>
      )}

      {/* Bottom area: viewer count for own stories, reactions + reply for others */}
      {isOwnStory ? (
        <button
          onClick={() => canSeeViewers ? setViewersSheet(story.viewers ?? []) : setProGate(true)}
          className="flex items-center gap-1.5 px-5 pb-4 text-white/60 hover:text-white transition-colors text-sm"
        >
          <FiEye className="w-4 h-4" />
          <span>{story.viewCount ?? 0} {(story.viewCount ?? 0) === 1 ? 'view' : 'views'}</span>
        </button>
      ) : (
        <div className="px-4 pb-6 pt-2 space-y-2.5">
          {/* Quick emoji reactions */}
          <div className="flex gap-4 justify-center">
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleReaction(emoji)}
                className="text-2xl active:scale-90 transition-transform"
              >
                {emoji}
              </button>
            ))}
          </div>

          {/* Text reply input */}
          <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2.5 border border-white/15">
            <input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onFocus={pauseProgress}
              onBlur={() => { if (!replyText.trim()) resumeProgress(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleReply(); }}
              placeholder={`Reply to ${group.profile?.firstName ?? 'story'}…`}
              className="flex-1 bg-transparent text-white text-sm placeholder-white/40 outline-none"
            />
            {replyText.trim() && (
              <button
                onClick={handleReply}
                className="text-white/60 hover:text-white transition-colors flex-shrink-0"
              >
                <FiSend className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Send feedback */}
          {sent && (
            <p className="text-center text-white/50 text-xs">{sent}</p>
          )}
        </div>
      )}

      {/* Hidden navigation hints on edges */}
      <button
        onClick={handlePrev}
        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-white transition-colors pointer-events-none opacity-0"
        aria-hidden
      >
        <FiChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={advanceStory}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-white transition-colors pointer-events-none opacity-0"
        aria-hidden
      >
        <FiChevronRight className="w-6 h-6" />
      </button>

      {/* Pro gate sheet */}
      {proGate && (
        <div
          className="absolute inset-0 z-20 flex items-end justify-center bg-black/70"
          onClick={() => setProGate(false)}
        >
          <div
            className="w-full bg-[#1a1a1a] rounded-t-3xl border-t border-white/10 pb-10 px-5 pt-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-2xl bg-yellow/15 flex items-center justify-center">
                <FiStar className="w-6 h-6 text-yellow" />
              </div>
            </div>
            <h3 className="text-white font-bold text-lg text-center mb-2">Upgrade to Pro</h3>
            <p className="text-white/50 text-sm text-center mb-6 leading-relaxed">
              See exactly who viewed your stories — names, photos, and timestamps — with a Pro subscription.
            </p>
            <button
              onClick={() => { setProGate(false); dispatch(closeViewer()); navigate('/subscribe'); }}
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-yellow to-magenta text-white font-bold text-sm mb-3 active:scale-95 transition-transform"
            >
              Upgrade Now
            </button>
            <button
              onClick={() => setProGate(false)}
              className="w-full py-3 rounded-2xl bg-white/5 text-white/50 font-semibold text-sm active:scale-95 transition-transform"
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Viewers sheet */}
      {viewersSheet !== null && (
        <div
          className="absolute inset-0 z-10 flex items-end justify-center bg-black/60"
          onClick={() => setViewersSheet(null)}
        >
          <div
            className="w-full bg-[#1a1a1a] rounded-t-3xl border-t border-white/10 pb-8 max-h-[60vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
              <h3 className="font-semibold text-white text-base">
                {viewersSheet.length === 0 ? 'No views yet' : `${viewersSheet.length} viewer${viewersSheet.length !== 1 ? 's' : ''}`}
              </h3>
              <button onClick={() => setViewersSheet(null)} className="text-white/40 hover:text-white p-1">
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-3 space-y-3">
              {viewersSheet.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-8">No one has viewed this story yet.</p>
              ) : (
                viewersSheet.map((v) => (
                  <div key={v.userId} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                      {v.profilePhoto ? (
                        <img src={v.profilePhoto} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FiUser className="w-4 h-4 text-white/30" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{v.firstName ?? 'User'}</p>
                      <p className="text-white/35 text-xs">
                        {new Date(v.viewedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
